import React, { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Switch } from '@/components/ui/switch';
import { showSuccess, showError } from '@/utils/toast';
import { PlusCircle, Edit } from 'lucide-react';
import { getCsrfToken } from '@/utils/csrf';
import Header from '@/components/layout/Header';
import Footer from '@/components/layout/Footer';
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogFooter,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Save } from 'lucide-react';
import RichTextEditor from '@/components/RichTextEditor'; // Importáljuk az új szerkesztőt

interface SiteUpdate {
    id: number;
    title: string;
    content: string;
    active: boolean;
    created_at: string;
}

const AdminSiteUpdates: React.FC = () => {
    const [updates, setUpdates] = useState<SiteUpdate[]>([]);
    const [isLoading, setIsLoading] = useState(true);
    const [editingUpdate, setEditingUpdate] = useState<Partial<SiteUpdate> | null>(null); // This will control the modal

    const fetchUpdates = async () => {
        try {
            const response = await fetch('/api/admin/site_updates');
            if (!response.ok) throw new Error('Failed to fetch updates');
            const data = await response.json();
            setUpdates(data);
        } catch (error) {
            showError(error instanceof Error ? error.message : 'An unknown error occurred');
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        fetchUpdates();
    }, []);

    const handleSave = async () => {
        if (!editingUpdate || !editingUpdate.title || !editingUpdate.content) {
            showError('A cím és a tartalom megadása kötelező.');
            return;
        }

        const isNew = !editingUpdate.id;
        const url = isNew ? '/api/admin/site_updates' : `/api/admin/site_updates/${editingUpdate.id}`;
        const method = isNew ? 'POST' : 'PATCH';

        try {
            const response = await fetch(url, {
                method,
                headers: {
                    'Content-Type': 'application/json',
                    'X-CSRF-Token': getCsrfToken(),
                },
                body: JSON.stringify({ site_update: editingUpdate }),
            });

            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.errors.join(', '));
            }

            showSuccess('Mentés sikeres!');
            setEditingUpdate(null);
            fetchUpdates();
        } catch (error) {
            showError(error instanceof Error ? error.message : 'Mentési hiba');
        }
    };

    const handleToggleActive = async (update: SiteUpdate) => {
        try {
            const response = await fetch(`/api/admin/site_updates/${update.id}`, {
                method: 'PATCH',
                headers: {
                    'Content-Type': 'application/json',
                    'X-CSRF-Token': getCsrfToken(),
                },
                body: JSON.stringify({ site_update: { active: !update.active } }),
            });

            if (!response.ok) throw new Error('Status update failed');
            showSuccess('Státusz frissítve!');
            fetchUpdates();
        } catch (error) {
            showError(error instanceof Error ? error.message : 'Hiba a státusz frissítésekor');
        }
    };

    if (isLoading) return <div>Betöltés...</div>;

    return (
        <div className="min-h-screen flex flex-col">
            <Header />
            <main className="flex-grow container mx-auto p-4 py-8 main-content-bg mt-[12px]">
                <div className="flex justify-between items-center mb-4">
                    <h1 className="text-2xl font-bold">Oldal frissítések</h1>
                    <Button onClick={() => setEditingUpdate({ title: '', content: '', active: false })}>
                        <PlusCircle className="mr-2 h-4 w-4" /> Új frissítés
                    </Button>
                </div>
                <div className="space-y-4">
                    {updates.map((update) => (
                        <Card key={update.id}>
                            <CardHeader>
                                <CardTitle className="flex justify-between items-center">
                                    {update.title}
                                    <div className="flex items-center gap-2">
                                        <Switch checked={update.active} onCheckedChange={() => handleToggleActive(update)} />
                                        <Button variant="ghost" size="icon" onClick={() => setEditingUpdate(update)}>
                                            <Edit className="h-4 w-4" />
                                        </Button>
                                    </div>
                                </CardTitle>
                            </CardHeader>
                            <CardContent>
                                <div className="prose prose-invert max-w-none" dangerouslySetInnerHTML={{ __html: update.content }} />
                                <p className="text-sm text-muted-foreground mt-4">Létrehozva: {new Date(update.created_at).toLocaleString()}</p>
                            </CardContent>
                        </Card>
                    ))}
                </div>
            </main>

            {editingUpdate && (
                <Dialog open={!!editingUpdate} onOpenChange={(isOpen) => !isOpen && setEditingUpdate(null)}>
                    <DialogContent className="sm:max-w-4xl max-h-[90vh] overflow-y-auto">
                        <DialogHeader>
                            <DialogTitle>{editingUpdate.id ? 'Frissítés szerkesztése' : 'Új frissítés létrehozása'}</DialogTitle>
                        </DialogHeader>
                        <div className="space-y-4 py-4">
                            <Input
                                placeholder="Cím"
                                value={editingUpdate.title || ''}
                                onChange={(e) => setEditingUpdate({ ...editingUpdate, title: e.target.value })}
                            />
                            <RichTextEditor
                                content={editingUpdate.content || ''}
                                onChange={(content) => setEditingUpdate({ ...editingUpdate, content })}
                            />
                            <div className="flex items-center space-x-2">
                                <Switch
                                    id="active-switch-modal"
                                    checked={editingUpdate.active || false}
                                    onCheckedChange={(checked) => setEditingUpdate({ ...editingUpdate, active: checked })}
                                />
                                <Label htmlFor="active-switch-modal">Aktív</Label>
                            </div>
                        </div>
                        <DialogFooter className="pt-4">
                            <Button variant="outline" onClick={() => setEditingUpdate(null)}>Mégse</Button>
                            <Button onClick={handleSave}><Save className="mr-2 h-4 w-4" /> Mentés</Button>
                        </DialogFooter>
                    </DialogContent>
                </Dialog>
            )}

            <Footer />
        </div>
    );
};

export default AdminSiteUpdates;